import { Autocomplete, FormControl, FormLabel, TextField } from '@mui/material';
import { useEffect } from 'react';

import { useDAL } from '@/contexts/useDAL';

import { EmptySelect } from './emptySelect';

export const SecurityGroupSelect = ({
  accountId,
  regionName,
  securityGroupIds,
  onChange,
  vpc,
  subnetId,
  isSubnetIdMandatory,
  isOptionalField,
  hideLabel,
}: {
  accountId?: string;
  regionName?: string;
  securityGroupIds?: string[];
  vpc: string;
  subnetId?: string;
  isSubnetIdMandatory?: boolean;
  onChange: (securityGroupIds?: string[]) => void;
  isOptionalField?: boolean;
  hideLabel?: boolean;
}) => {
  const title = `Security groups${isOptionalField ? ' (optional)' : ''}`;
  if (!accountId || !regionName || !vpc || (isSubnetIdMandatory && !subnetId)) {
    return <EmptySelect title={hideLabel ? '' : title} />;
  }

  return (
    <InnerSecurityGroupSelect
      vpc={vpc}
      subnetId={subnetId}
      accountId={accountId}
      regionName={regionName}
      securityGroupIds={securityGroupIds}
      onChange={onChange}
      title={title}
      hideLabel={hideLabel}
    />
  );
};

const InnerSecurityGroupSelect = ({
  vpc,
  subnetId,
  accountId,
  regionName,
  securityGroupIds,
  onChange,
  title,
  hideLabel,
}: {
  accountId: string;
  regionName: string;
  vpc?: string;
  subnetId?: string;
  securityGroupIds?: string[];
  onChange: (securityGroupIds?: string[]) => void;
  title: string;
  hideLabel?: boolean;
}) => {
  const dal = useDAL();
  const { data: body, isLoading } = dal.restore.securityGroups.useList(
    accountId,
    regionName,
    vpc,
    subnetId
  );

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (
      securityGroupIds?.length &&
      securityGroupIds.some(
        (sg) => !body?.securityGroups?.map((x) => x.id).includes(sg)
      )
    ) {
      onChange(undefined);
    }
  }, [body?.securityGroups, isLoading, onChange, securityGroupIds]);

  if (!body?.securityGroups.length) {
    return <EmptySelect title={hideLabel ? '' : title} />;
  }

  const options = body.securityGroups.map((group) => ({
    value: group.id,
    label: group.name,
  }));

  return (
    <FormControl size='small' className='flex-1'>
      {!hideLabel && <FormLabel>{title}</FormLabel>}
      <Autocomplete
        data-testid='securityGroupSelect'
        multiple
        limitTags={1}
        clearOnBlur
        size='small'
        value={options.filter((x) =>
          (securityGroupIds || []).includes(x.value)
        )}
        onChange={(_, newValue) => onChange(newValue.map((x) => x.value))}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              placeholder={
                (securityGroupIds || []).length === 0 ? 'Select' : undefined
              }
              label=''
              InputLabelProps={{ shrink: true }}
            />
          );
        }}
        options={options}
      />
    </FormControl>
  );
};
